import React, { useEffect, useState } from 'react';
import { Loader2, Save, Trophy } from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from './lib/permissions';

// Oyun sayfasının "Son Kazançlar" tablosunun eşiği: yalnızca bahsin en az bu
// katını kazandıran tur listelenir. Değer `settings` kaydında değil
// `provider_configs`'tedir (bkz. backend/services/game_wins.py), bu yüzden
// sekmenin ortak Kaydet düğmesine bağlanmaz: kendi ucu ve düğmesi vardır.
export const GAME_WINS_DEFAULT = 10;

export function parseWinThreshold(raw, min = 1, max = 1000) {
  const text = String(raw ?? '').trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const value = Number(text);
  if (value < min || value > max) return null;
  return value;
}

const GameWinsSettingsPanel = () => {
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  const canWrite = can('games.write');
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState(String(GAME_WINS_DEFAULT));
  const [bounds, setBounds] = useState({ min: 1, max: 1000 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    client.get('/admin/game-wins')
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data.min_multiplier);
        setValue(String(data.min_multiplier));
        setBounds({ min: data.min ?? 1, max: data.max ?? 1000 });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (saved === null) return null;

  const parsed = parseWinThreshold(value, bounds.min, bounds.max);
  const dirty = parsed !== null && parsed !== saved;

  const save = async () => {
    if (parsed === null) return;
    setSaving(true);
    try {
      const { data } = await client.patch('/admin/game-wins', { min_multiplier: parsed });
      setSaved(data.min_multiplier);
      setValue(String(data.min_multiplier));
      toast({ title: '"Son Kazançlar" eşiği kaydedildi' });
    } catch (e) {
      toast({
        title: '"Son Kazançlar" eşiği kaydedilemedi',
        description: e.response?.data?.detail?.[0]?.msg
          || e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="gc-panel is-tone" style={{ '--gc-tone': '#f59e0b' }}>
      <div className="gc-panel-head">
        <span className="gc-card-icon"><Trophy size={19} /></span>
        <div className="gc-panel-title">
          <h2>Oyun Sayfası — Son Kazançlar</h2>
          <p>
            Oyun sayfasının altındaki tabloya hangi kazançların gireceğini seçin.
          </p>
        </div>
      </div>

      <div className="gc-panel-body">
        <label className="gc-field">
          <span className="gc-label">En Düşük Kazanç Çarpanı (x)</span>
          <input
            type="text"
            inputMode="decimal"
            value={value}
            disabled={!canWrite}
            onChange={(event) => setValue(event.target.value)}
            className="gc-input"
            aria-label="Son Kazançlar en düşük kazanç çarpanı"
          />
          <span className="gc-hint">
            Tabloda yalnızca bahsin en az bu katını kazandıran turlar görünür
            (10 yazılırsa 2 ₺ bahisle 20 ₺ ve üzeri). Varsayılan {GAME_WINS_DEFAULT}x;
            en az {bounds.min}, en fazla {bounds.max}. Bedava dönen turlar
            (freespin) bahsi olmadığı için çarpanı hesaplanamaz ve eşik ne olursa
            olsun listelenmez. Değişiklik oyun sayfasına en geç 20 saniyede yansır.
          </span>
          {parsed === null && (
            <span className="gc-hint is-warn">
              {bounds.min} ile {bounds.max} arasında bir sayı girin (en çok iki ondalık).
            </span>
          )}
        </label>
      </div>

      {canWrite && (
        <div className="gc-panel-foot">
          <span className="gc-foot-note">Şu an kayıtlı eşik: {saved}x</span>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="gc-btn is-primary"
          >
            {saving ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
            Eşiği Kaydet
          </button>
        </div>
      )}
    </section>
  );
};

export default GameWinsSettingsPanel;
